import { useState, useEffect, useCallback, useRef } from 'react';
import { AudioAnalyzer, type AudioAnalysisResult } from '../utils/audioAnalysis';

export interface UseAudioWaveformOptions {
  segments?: number;
  scaleFactor?: number;
  fallbackToSimple?: boolean;
}

export interface UseAudioWaveformReturn {
  waveformData: number[];
  isLoading: boolean;
  isAnalyzed: boolean;
  error: string | null;
  retryAnalysis: () => void;
  cacheKey: string | null;
}

export const useAudioWaveform = (audioUrl: string | null, options: UseAudioWaveformOptions = {}): UseAudioWaveformReturn => {
  const { segments = 50, scaleFactor = 12, fallbackToSimple = true } = options;

  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cacheKey, setCacheKey] = useState<string | null>(null);

  const currentUrlRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const analyzeAudio = useCallback(
    async (url: string) => {
      // Abort any ongoing analysis
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setIsLoading(true);
      setError(null);
      setIsAnalyzed(false);
      currentUrlRef.current = url;

      try {
        // Check if request was aborted before starting
        if (signal.aborted) return;

        const result: AudioAnalysisResult = await AudioAnalyzer.analyzeAudio(url, segments, scaleFactor);

        // Check if this is still the current URL and not aborted
        if (currentUrlRef.current === url && !signal.aborted) {
          setWaveformData(result.waveformData);
          setIsAnalyzed(result.success);
          
          // Store cache key if available
          if (result.cacheKey) {
            setCacheKey(result.cacheKey);
          }

          if (!result.success && result.error) {
            setError(result.error);

            // If analysis failed and fallback is enabled, show simple waveform
            if (fallbackToSimple) {
              setWaveformData(AudioAnalyzer.generateSimpleWaveform(segments));
            }
          }
        }
      } catch (err) {
        if (currentUrlRef.current === url && !signal.aborted) {
          const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
          setError(errorMessage);

          if (fallbackToSimple) {
            setWaveformData(AudioAnalyzer.generateSimpleWaveform(segments));
          }
        }
      } finally {
        if (currentUrlRef.current === url && !signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [segments, scaleFactor, fallbackToSimple]
  );

  const retryAnalysis = useCallback(() => {
    if (audioUrl) {
      analyzeAudio(audioUrl);
    }
  }, [audioUrl, analyzeAudio]);

  useEffect(() => {
    if (audioUrl) {
      analyzeAudio(audioUrl);
    } else {
      // Reset state when no URL
      setWaveformData([]);
      setIsLoading(false);
      setIsAnalyzed(false);
      setError(null);
      setCacheKey(null);
      currentUrlRef.current = null;
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [audioUrl, analyzeAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    waveformData,
    isLoading,
    isAnalyzed,
    error,
    retryAnalysis,
    cacheKey,
  };
};
